<script setup lang="ts">
import { InformationCircleIcon } from "@heroicons/vue/24/outline";
import { useVuelidate } from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { useVModel } from "@vueuse/core";
import Checkbox from "primevue/checkbox";
import InputNumber from "primevue/inputnumber";
import Skeleton from "primevue/skeleton";
import { computed, type Ref } from "vue";
import { useToast } from "vue-toast-notification";
import HelpIconPrimeVueTooltip from "@/components/shared/Tooltip/HelpIconPrimeVueTooltip.vue";
import { hasShowTenantPaymentProcessingEnabled } from "@/composables/authorization.js";
import { useConfigurableSessionPayments } from "@/composables/useConfigurableSessionPayments";
import { APPLICATION_FEE_DATA, CONFIRMATION_PAYMENT_PRICE_IN_CENTS, ERROR_TIMEOUT } from "@/constants";
import { dollarsToCents } from "@/mixins/helpers";
import { useGetSingleEntitySupplement } from "@/providers/entitySupplement/useGetSingleEntitySupplement";
import {
  OWNER_SUPPLEMENT_STALE_TIME_MS,
  SESSION_FEE_NON_NEGATIVE_MESSAGE,
  SESSION_FEE_PAYMENT_PROCESSING_DISABLED_MESSAGE,
  SESSION_FEE_PAYMENT_SETTINGS_ERROR_MESSAGE,
  SESSION_FEE_TOOLTIPS,
  SESSION_FEES_VUELIDATE_REGISTER_AS,
} from "./constants";
import { applicationRequirementsFeesPaymentsPresets } from "./styles";
import {
  type ApplicationRequirementsFeesPaymentsProps,
  feeCheckboxIsOnFromSessionCents,
  type ProgramSessionConfigurableFeesModel,
} from "./types";
import { useSuppressedSessionFees } from "./useSuppressedSessionFees";
import { commitSessionFeeDollarsFromInput, sessionFeeDollarsForInput } from "./utils";

defineOptions({
  name: "ApplicationRequirementsFeesPayments",
});

const props = withDefaults(defineProps<ApplicationRequirementsFeesPaymentsProps>(), {
  canEditFees: true,
});

const emit = defineEmits<{
  "update:modelValue": [value: ProgramSessionConfigurableFeesModel];
}>();

const programSession = useVModel(props, "modelValue", emit) as Ref<ProgramSessionConfigurableFeesModel>;

const toast = useToast();

// Authoritative: updateSessionOwnerEntity writes this the moment the owner dropdown changes.
const resolvedOwnerEntityId = computed<string>(() => programSession.value?.owner_entity_id ?? "");

const supplementQueryEnabled = computed<boolean>(
  () => Boolean(hasShowTenantPaymentProcessingEnabled.value) && Boolean(resolvedOwnerEntityId.value),
);

const { data: ownerEntitySupplement, isLoading: ownerEntitySupplementLoading } = useGetSingleEntitySupplement({
  id: resolvedOwnerEntityId,
  useQueryOptions: {
    enabled: supplementQueryEnabled,
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: OWNER_SUPPLEMENT_STALE_TIME_MS,
    throwOnError: () => {
      toast.open({
        message: SESSION_FEE_PAYMENT_SETTINGS_ERROR_MESSAGE,
        type: "error",
        position: "bottom",
        duration: ERROR_TIMEOUT,
      });
      return false;
    },
  },
});

useConfigurableSessionPayments(programSession);

const paymentProcessingSuppressed = computed<boolean>(() => {
  if (!hasShowTenantPaymentProcessingEnabled.value) {
    return false;
  }
  // `=== false`, not `!enabled`: undefined means loading, failed, or absent — none are opt-outs.
  return ownerEntitySupplement.value?.payment_processing_enabled === false;
});

useSuppressedSessionFees({
  programSession,
  suppressed: paymentProcessingSuppressed,
  selectedOwnerEntityId: resolvedOwnerEntityId,
  loading: ownerEntitySupplementLoading,
});

const feesEditable = computed<boolean>(
  () => props.canEditFees && !paymentProcessingSuppressed.value && !ownerEntitySupplementLoading.value,
);

const feeNonNegative = helpers.withMessage(
  SESSION_FEE_NON_NEGATIVE_MESSAGE,
  (v: number | null | undefined) =>
    v === undefined || v === null || (typeof v === "number" && v >= 0 && !Number.isNaN(v)),
);

const rules = computed(() => ({
  application_fee_in_cents: { feeNonNegative },
  confirmation_deposit_in_cents: { feeNonNegative },
}));

const v$ = useVuelidate(rules, programSession as never, {
  $registerAs: SESSION_FEES_VUELIDATE_REGISTER_AS,
  $lazy: true,
});

const applicationFeeEnabled = computed(() =>
  feeCheckboxIsOnFromSessionCents(programSession.value?.application_fee_in_cents),
);

const confirmationDepositEnabled = computed(() =>
  feeCheckboxIsOnFromSessionCents(programSession.value?.confirmation_deposit_in_cents),
);

const onApplicationFeeEnabledUpdate = (checked: boolean) => {
  if (!feesEditable.value) {
    return;
  }
  if (checked) {
    programSession.value.application_fee_in_cents = dollarsToCents(APPLICATION_FEE_DATA.amount);
  } else {
    programSession.value.application_fee_in_cents = undefined;
  }
};

const onConfirmationDepositEnabledUpdate = (checked: boolean) => {
  if (!feesEditable.value) {
    return;
  }
  if (checked) {
    programSession.value.confirmation_deposit_in_cents = CONFIRMATION_PAYMENT_PRICE_IN_CENTS;
  } else {
    programSession.value.confirmation_deposit_in_cents = undefined;
  }
};

const applicationFeeDollarsDisplay = sessionFeeDollarsForInput(programSession, "application_fee_in_cents");
const confirmationDepositDollarsDisplay = sessionFeeDollarsForInput(programSession, "confirmation_deposit_in_cents");

const onApplicationFeeDollarsUpdate = (val: number | null | undefined) => {
  commitSessionFeeDollarsFromInput(programSession, "application_fee_in_cents", val);
};

const onConfirmationDepositDollarsUpdate = (val: number | null | undefined) => {
  commitSessionFeeDollarsFromInput(programSession, "confirmation_deposit_in_cents", val);
};

const applicationFeeInputDisabled = computed(() => {
  return !feesEditable.value || !applicationFeeEnabled.value;
});

const confirmationDepositInputDisabled = computed(() => {
  return !feesEditable.value || !confirmationDepositEnabled.value;
});
</script>

<template>
  <div class="cgrid grid-cols-2 gap-x-24">
    <div class="grid grid-cols-2 gap-x-24 gap-y-6">
      <div class="flex flex-col gap-2">
        <div class="flex flex-wrap items-center gap-3">
          <Checkbox
            :model-value="applicationFeeEnabled"
            input-id="session-application-fee-enabled"
            binary
            :disabled="!feesEditable"
            :pt="applicationRequirementsFeesPaymentsPresets.checkbox"
            @update:model-value="onApplicationFeeEnabledUpdate"
          />
          <label class="common-label-text" for="session-application-fee-enabled">Application Fee</label>
          <div class="flex self-center ml-1">
            <HelpIconPrimeVueTooltip :message="SESSION_FEE_TOOLTIPS.feeType.application" />
          </div>
        </div>
        <div v-if="ownerEntitySupplementLoading" class="flex max-w-xs flex-col gap-1">
          <Skeleton height="2.5rem" class="w-full" />
        </div>
        <div v-else class="flex max-w-xs flex-col gap-1">
          <InputNumber
            :model-value="applicationFeeDollarsDisplay"
            input-id="session-application-fee-amount"
            @update:model-value="onApplicationFeeDollarsUpdate"
            mode="currency"
            currency="USD"
            locale="en-US"
            :min="0"
            :min-fraction-digits="2"
            :max-fraction-digits="2"
            :disabled="applicationFeeInputDisabled"
            class="w-full"
            input-class="h-10 w-full rounded border border-gray-300 px-3"
          />
          <span v-if="v$.application_fee_in_cents.$error" class="text-sm text-red-100">
            {{ v$.application_fee_in_cents.$errors[0]?.$message }}
          </span>
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <div class="flex flex-wrap items-center gap-3">
          <Checkbox
            :model-value="confirmationDepositEnabled"
            input-id="session-confirmation-deposit-enabled"
            binary
            :disabled="!feesEditable"
            :pt="applicationRequirementsFeesPaymentsPresets.checkbox"
            @update:model-value="onConfirmationDepositEnabledUpdate"
          />
          <label class="common-label-text" for="session-confirmation-deposit-enabled"
            >Confirmation Deposit</label
          >
          <div class="flex self-center ml-1">
            <HelpIconPrimeVueTooltip :message="SESSION_FEE_TOOLTIPS.feeType.confirmation" />
          </div>
        </div>
        <div v-if="ownerEntitySupplementLoading" class="flex max-w-xs flex-col gap-1">
          <Skeleton height="2.5rem" class="w-full" />
        </div>
        <div v-else class="flex max-w-xs flex-col gap-1">
          <InputNumber
            :model-value="confirmationDepositDollarsDisplay"
            input-id="session-confirmation-deposit-amount"
            @update:model-value="onConfirmationDepositDollarsUpdate"
            mode="currency"
            currency="USD"
            locale="en-US"
            :min="0"
            :min-fraction-digits="2"
            :max-fraction-digits="2"
            :disabled="confirmationDepositInputDisabled"
            class="w-full"
            input-class="h-10 w-full rounded border border-gray-300 px-3"
          />
          <span v-if="v$.confirmation_deposit_in_cents.$error" class="text-sm text-red-100">
            {{ v$.confirmation_deposit_in_cents.$errors[0]?.$message }}
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="paymentProcessingSuppressed"
      class="mt-4 flex items-start"
      data-testid="session-fees-payment-processing-disabled-note"
    >
      <InformationCircleIcon class="h-5 w-5 mr-2 shrink-0" />
      <span class="text-sm text-gray-700">{{ SESSION_FEE_PAYMENT_PROCESSING_DISABLED_MESSAGE }}</span>
    </div>
  </div>
</template>
